/*
  Modèle de données du terrain.

  ╔══════════════════════════════════════════════════════════════════════╗
  ║  RÈGLE NON NÉGOCIABLE                                                ║
  ║                                                                      ║
  ║  Aucune coordonnée géographique n'existe dans ce système.            ║
  ║  Pas de latitude, pas de longitude, pas de geojson, pas de champ     ║
  ║  « précision GPS », ni ici ni en base ni sur le réseau.              ║
  ║                                                                      ║
  ║  L'API Geolocation du navigateur n'est jamais appelée, et aucune     ║
  ║  permission de localisation n'est demandée.                          ║
  ║                                                                      ║
  ║  Le braconnage d'œufs est réel : deux personnes ont été appréhendées ║
  ║  avec 35 œufs en 2025. La protection n'est pas un contrôle d'accès,  ║
  ║  c'est une absence de modélisation. On ne peut pas faire fuiter      ║
  ║  ce qu'on n'a jamais stocké.                                         ║
  ║                                                                      ║
  ║  La localisation est un nom de secteur, et rien d'autre.             ║
  ║  Vérifié par tests/noeud/sans-geo.test.ts                            ║
  ╚══════════════════════════════════════════════════════════════════════╝
*/

/** Code de secteur, ex. `salines_centre`. La liste fait autorité côté serveur. */
export type CodeSecteur = string

export const TYPES_OBSERVATION = [
  'personne_sensibilisee',
  'ponte',
  'trace_sans_ponte',
  'emergence',
] as const
export type TypeObservation = (typeof TYPES_OBSERVATION)[number]

export const ESPECES = ['luth', 'olivatre', 'verte'] as const
export type Espece = (typeof ESPECES)[number]

export const TYPES_SIGNALEMENT = [
  'eclairage',
  'chien_divagation',
  'emergence_desorientee',
  'tortue_blessee',
] as const
export type TypeSignalement = (typeof TYPES_SIGNALEMENT)[number]

/** Horodatage ISO 8601 en UTC. Toujours une chaîne, jamais un Date stocké. */
export type Horodatage = string

export interface Patrouille {
  id: string
  secteur: CodeSecteur
  debut: Horodatage
  /** `null` tant que la patrouille est en cours. */
  fin: Horodatage | null
  /** Prénoms du binôme, libre et facultatif. Jamais de nom de famille. */
  equipe: string | null
}

export interface Observation {
  id: string
  patrouille_id: string
  type: TypeObservation
  /** Obligatoire si `type === 'ponte'`, interdite sinon. */
  espece: Espece | null
  quantite: number
  ts: Horodatage
}

export interface Signalement {
  id: string
  type: TypeSignalement
  secteur: CodeSecteur
  quantite: number | null
  note: string | null
  photo_id: string | null
  ts: Horodatage
}

export interface PhotoLocale {
  id: string
  /** Déjà retaillée à 1280 px et réencodée : sans EXIF, donc sans GPS. */
  blob: Blob
  envoye: boolean
}

export interface SecteurZonage {
  code: CodeSecteur
  libelle: string
  groupe: string | null
  ordre: number
}

/** Opérations de synchronisation. L'`id` de l'événement est l'`id` de l'entité. */
export const OPS = [
  'patrouille.ouvrir',
  'patrouille.cloturer',
  'observation.creer',
  'signalement.creer',
  'photo.televerser',
] as const
export type OpSync = (typeof OPS)[number]

export type EtatEvenement = 'en_attente' | 'quarantaine'

export interface EvenementFile {
  /** Généré côté client. Sert de clé d'idempotence de bout en bout. */
  id: string
  op: OpSync
  donnees: Record<string, unknown>
  cree_le: Horodatage
  /** Ordre d'insertion : garantit un envoi FIFO même si les uuid sont aléatoires. */
  rang: number
  essais: number
  /** Epoch ms. Un événement n'est pas repris avant cette date. */
  prochain_essai_le: number
  etat: EtatEvenement
  /** Renseigné uniquement en quarantaine. */
  raison?: string
}

export interface TotauxSaison {
  saison: string
  pontes: Record<Espece, number>
  traces_sans_ponte: number
  emergences: number
  personnes_sensibilisees: number
  signalements: Record<TypeSignalement, number>
  genere_le: Horodatage
}
